import {
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type ColumnVisibilityState,
  functionalUpdate,
  type Table,
  useTable,
} from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { useEffect, useMemo, useState } from 'react';

import {
  conversationsTableColumns,
  conversationsTableFeatures,
  type ConversationsTableFeatures,
} from '../conversationsTable.columns';

import type { UseConversationsTableLayoutResult } from './useConversationsTableLayout';

const columnIds = conversationsTableColumns.flatMap((column) =>
  column.id ? [column.id] : [],
);

const endPinnedColumnIds = ['actions'];

const alwaysVisibleColumnIds = conversationsTableColumns.flatMap((column) =>
  column.enableHiding === false && column.id ? [column.id] : [],
);

/**
 * Returns the column and row model that the conversations table renders, in
 * the user's layout when given one and in the default layout otherwise.
 */
export const useConversationsTable = (
  conversations: readonly SearchConversationEnriched[],
  layout?: UseConversationsTableLayoutResult,
): Table<ConversationsTableFeatures, SearchConversationEnriched> => {
  const savedColumnOrder = layout?.columnOrder;
  const savedColumns = layout?.columns;

  // Spells out the columns missing from the saved order, which TanStack puts last, so the pinned columns follow the same order.
  const columnOrder = useMemo(
    (): ColumnOrderState => [
      ...(savedColumnOrder ?? []),
      ...columnIds.filter((columnId) => !savedColumnOrder?.includes(columnId)),
    ],
    [savedColumnOrder],
  );

  const savedColumnSizing = useMemo(
    (): ColumnSizingState =>
      Object.fromEntries(
        Object.entries(savedColumns ?? {}).flatMap(([columnId, column]) =>
          column.width ? [[columnId, column.width]] : [],
        ),
      ),
    [savedColumns],
  );

  // The widths change on every pointer move while a column is resized, so they're only saved once it's let go.
  const [resizedColumnSizing, setResizedColumnSizing] =
    useState<ColumnSizingState>();

  // TanStack hides any column the state says to, even one that can't be hidden.
  const columnVisibility = useMemo(
    (): ColumnVisibilityState =>
      Object.fromEntries(
        Object.entries(savedColumns ?? {}).map(([columnId, column]) => [
          columnId,
          !column.isHidden || alwaysVisibleColumnIds.includes(columnId),
        ]),
      ),
    [savedColumns],
  );

  const columnPinning = useMemo(
    (): ColumnPinningState => ({
      start: columnOrder.filter(
        (columnId) => savedColumns?.[columnId]?.isPinned,
      ),
      end: endPinnedColumnIds,
    }),
    [columnOrder, savedColumns],
  );

  const table = useTable({
    features: conversationsTableFeatures,
    data: conversations,
    columns: conversationsTableColumns,
    getRowId: (conversation) => conversation.id.toString(),
    defaultColumn: { minSize: 80 },
    enableColumnResizing: Boolean(layout),
    columnResizeMode: 'onChange',
    state: {
      columnOrder,
      columnSizing: resizedColumnSizing ?? savedColumnSizing,
      columnVisibility,
      columnPinning,
    },
    onColumnSizingChange: (updater) =>
      setResizedColumnSizing((columnSizing) =>
        functionalUpdate(updater, columnSizing ?? savedColumnSizing),
      ),
    meta: { layout },
  });

  const isResizing = Boolean(table.state.columnResizing.isResizingColumn);

  useEffect(() => {
    if (resizedColumnSizing && !isResizing) {
      layout?.setColumnSizing(resizedColumnSizing);
      setResizedColumnSizing(undefined);

      Object.entries(resizedColumnSizing)
        .filter(([columnId, width]) => width !== savedColumnSizing[columnId])
        .forEach(([columnId]) =>
          ampli.conversationsTableColumnResized({ column: columnId }),
        );
    }
  }, [resizedColumnSizing, isResizing, layout, savedColumnSizing]);

  return table;
};
